import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	ResultClickedCallback,
	ResultClickedCallbackParams,
} from '../../../common/constants/types';
import {
	type AdvancedSearchResultInteractedMetadata,
	onAdvancedSearchResultLinkContextMenuClicked,
} from '../../../common/utils/events/advanced-search-result-interacted';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import { usePrimaryProduct } from '../../store/bootstrap';

interface FireResultCtxMenuClickedProps {
	metadata: AdvancedSearchResultInteractedMetadata;
	/** The index in which the result was shown to the user */
	position?: number;
	socialSignal?: string;
	isImpersonated?: boolean;
	is3PClickIngestionEnabled?: boolean;
}

/**
 * TODO this whole file is a copy of useFireResultClicked, we should add a parameter to  useFireResultClicked to allow it to be used for both left and right clicks
 * while doing the above, it's good to move all the logic for 3P products to onAdvancedSearchResultLinkClicked and change its name to getAdvancedSearchResultClickedAnalyticsPayload
 */

/**
 * @returns a callback to fire an event when the result
 * conext menu is clicked by the user, i.e. they right
 * click a result.
 */
export const useFireResultCtxMenuClicked = ({
	metadata,
	position,
	socialSignal,
	isImpersonated = false,
	is3PClickIngestionEnabled,
}: FireResultCtxMenuClickedProps): ResultClickedCallback => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionTriggered = fg('intent_detection_triggered_metrics_enabled')
		? isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale)
		: undefined;

	return useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const {
				sublistPosition,
				linkDocId,
				provider,
				linkDocContentType,
				fanoutProduct,
				fanoutLinkCategory,
				thirdPartyId,
			} = params;
			const analyticsPayload = onAdvancedSearchResultLinkContextMenuClicked({
				metadata,
				position,
				fanoutProduct,
				fanoutLinkCategory,
				socialSignal,
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
				sublistPosition,
				provider,
				linkDocId,
				linkDocContentType,
				isImpersonated: isImpersonated ?? false,
				is3PClickIngestionEnabled,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
				thirdPartyId,
			});
			fireAnalyticsEvent(analyticsPayload);
		},
		[
			fireAnalyticsEvent,
			metadata,
			position,
			socialSignal,
			commonAttributes,
			nonPrivacySafeAttributes,
			isImpersonated,
			is3PClickIngestionEnabled,
			intentDetectionTriggered,
		],
	);
};
